import { useContext, useState } from "react";
import { OverlayTrigger } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { withTranslation } from "react-i18next";
import { BsFileEarmarkSpreadsheet } from "react-icons/bs";
import { LoaderContext } from "../../../App";
import { LS_COMPANY_INFO_KEY } from "../../../core/constants/common";
import useHasPermission from "../../../core/hooks/useHasPermission";
import FormService from "../../../core/service/FormService";
import Utils from "../../../core/utilities/Utils";
import DataCollectionSummaryCard from "./DataCollectionSummaryCard";
import "./HomePage.scss";
import "./DataCollectionOverviewCard.scss";

const MONTH_NAME_TO_NUM = { JANUARY: 1, FEBRUARY: 2, MARCH: 3, APRIL: 4, MAY: 5, JUNE: 6, JULY: 7, AUGUST: 8, SEPTEMBER: 9, OCTOBER: 10, NOVEMBER: 11, DECEMBER: 12 };

function getFyPeriodFromCompanyInfo(selectedYear, financialYearStart, financialYearEnd) {
    const startMonth = MONTH_NAME_TO_NUM[financialYearStart] ?? 4;
    const endMonth = MONTH_NAME_TO_NUM[financialYearEnd] ?? 3;
    const y = Number(selectedYear) || new Date().getFullYear();
    const pad = (n) => String(n).padStart(2, '0');
    let startYear = y;
    let endYear = y;
    if (startMonth > endMonth) {
        startYear = y - 1;
        endYear = y;
    }
    const lastDay = new Date(endYear, endMonth, 0).getDate();
    return {
        periodStart: `${startYear}-${pad(startMonth)}-01`,
        periodEnd: `${endYear}-${pad(endMonth)}-${pad(lastDay)}`
    };
}

const DataCollectionOverviewCard = ({ t, selectedYear, displayYear }) => {
    const yearLabel = displayYear ?? selectedYear;
    const navigate = useNavigate();
    const { hasDataCollectionViewPermission } = useHasPermission();
    const [isDetailsExpanded, setIsDetailsExpanded] = useState(false);
    const { setLoaderDisplay } = useContext(LoaderContext);

    const gotoDataManagement = (e) => {
        e?.preventDefault();
        navigate("/company_admin/dataview");
    };

    const getPeriodLabel = () => {
        const companyInfo = JSON.parse(localStorage.getItem(LS_COMPANY_INFO_KEY) || '{}');
        const dataFreqType = companyInfo?.frequencyType ?? companyInfo?.dataFrequencyType;
        if (dataFreqType === 'FY') {
            const { periodEnd } = getFyPeriodFromCompanyInfo(selectedYear, companyInfo?.financialYearStart, companyInfo?.financialYearEnd);
            const endYear = periodEnd ? Number(periodEnd.slice(0, 4)) : Number(selectedYear);
            return `FY ${endYear - 1}–${String(endYear).slice(2)}`;
        }
        return `${yearLabel}`;
    };

    const getSelectedPeriod = () => {
        const companyInfo = JSON.parse(localStorage.getItem(LS_COMPANY_INFO_KEY) || '{}');
        const dataFreqType = companyInfo?.frequencyType ?? companyInfo?.dataFrequencyType;
        const isFinancialYear = dataFreqType === 'FY';
        return isFinancialYear
            ? getFyPeriodFromCompanyInfo(selectedYear, companyInfo?.financialYearStart, companyInfo?.financialYearEnd)
            : { periodStart: `${selectedYear}-01-01`, periodEnd: `${selectedYear}-12-31` };
    };

    const { periodStart, periodEnd } = getSelectedPeriod();

    const exportBranchSubmissionsSummary = () => {
        setLoaderDisplay(true);
        FormService.exportBranchSubmissionsSummary(periodStart, periodEnd).then((response) => {
            setLoaderDisplay(false);
            const blob = new Blob([response.data], { type: 'application/octet-stream' });
            const fileURL = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = fileURL;
            link.download = `Data Collection Status ${periodStart}_${periodEnd}.xlsx`;
            link.click();
            link.remove();
            window.URL.revokeObjectURL(fileURL);
        }).catch((error) => {
            setLoaderDisplay(false);
        });
    };

    return (
        <div className={`HomeCard mb-3 data-collection-overview-card${isDetailsExpanded ? ' data-collection-overview-card--expanded' : ''}`}>
            <div className="HomeCardhead">
                <div className="HomeCardhead_in">
                    <h5 className="mb-0">{t("DataCollectionOverview.label")} {t("CurrentStatusFor.label")} {getPeriodLabel()}</h5>
                    <OverlayTrigger placement="top" overlay={Utils.renderTooltip(t("ExportExcel.label"))}>
                        <button
                            type="button"
                            className="btn btn-link p-0"
                            onClick={exportBranchSubmissionsSummary}
                            aria-label={t("ExportExcel.label")}
                        >
                            <BsFileEarmarkSpreadsheet className="font-18" />
                        </button>
                    </OverlayTrigger>
                </div>
            </div>
            <div className="HomeCardBody">
                <div className="HomeCardBodyIn">
                    <DataCollectionSummaryCard
                        periodStart={periodStart}
                        periodEnd={periodEnd}
                        onToggleDetails={setIsDetailsExpanded}
                    />
                </div>
            </div>
            <div className="HomeCardFooter" hidden={!hasDataCollectionViewPermission()}>
                {t("ForMoreDetails.label")} <a href="#" onClick={gotoDataManagement}>{t("GoToDataManagement.label")}</a>
            </div>
        </div>
    );
};

export default withTranslation()(DataCollectionOverviewCard);
